/**
 * ForgotPasswordView — Request password reset email.
 */

import type { AweniContext } from '../../../context/AweniContext';
import { createAuthLayout } from '../components/AuthLayout';
import { createTurnstile, type TurnstileInstance } from '../components/TurnstileWidget';
import { validateEmail } from '../validators';
import { forgotPassword } from '../AuthService';

export async function mountForgotPasswordView(
  container: HTMLElement,
  ctx: AweniContext,
  opts: { onNavigate: (view: string, params?: Record<string, string>) => void },
): Promise<{ unmount: () => void }> {
  const t = ctx.i18n.t.bind(ctx.i18n);
  const layout = createAuthLayout(ctx);
  layout.setTitle(t('auth_forgot_password_title'));
  layout.setSubtitle(t('auth_forgot_password_subtitle'));

  let turnstile: TurnstileInstance | null = null;
  let loading = false;

  const form = document.createElement('form');
  form.setAttribute('novalidate', '');

  // Email
  const emailField = _createField('email', t('auth_email_label'), 'email', 'email');
  form.appendChild(emailField.wrapper);

  // Turnstile
  turnstile = await createTurnstile(ctx.theme.isDark);
  form.appendChild(turnstile.container);

  // Submit button
  const submitBtn = document.createElement('button');
  submitBtn.type = 'submit';
  submitBtn.className = 'auth-btn auth-btn-primary';
  submitBtn.innerHTML = `<span>${t('auth_reset_link_button')}</span>`;
  form.appendChild(submitBtn);

  // Back to login
  const backRow = document.createElement('div');
  backRow.className = 'auth-footer-row';
  const backLink = document.createElement('button');
  backLink.type = 'button';
  backLink.className = 'auth-link';
  backLink.textContent = t('auth_back_to_login');
  backLink.addEventListener('click', () => opts.onNavigate('login'));
  backRow.appendChild(backLink);
  form.appendChild(backRow);

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (loading) return;
    layout.clearMessages();

    const email = emailField.input.value.trim();
    const emailResult = validateEmail(email);
    if (!emailResult.valid) {
      layout.showError(t(emailResult.errors[0]));
      emailField.input.focus();
      return;
    }

    loading = true;
    submitBtn.disabled = true;
    submitBtn.innerHTML = `<span class="auth-spinner"></span>`;

    await forgotPassword(email, turnstile?.getToken() || undefined);

    // Always show success (anti-enumeration)
    layout.showSuccess(t('auth_reset_email_sent'));
    submitBtn.innerHTML = `<span>${t('auth_reset_link_button')}</span>`;
    submitBtn.disabled = false;
    loading = false;
  });

  layout.card.appendChild(form);
  container.innerHTML = '';
  container.appendChild(layout.wrapper);

  return {
    unmount() {
      turnstile?.destroy();
      container.innerHTML = '';
    },
  };
}

function _createField(
  name: string,
  label: string,
  type: string,
  autocomplete: string,
): { wrapper: HTMLElement; input: HTMLInputElement } {
  const wrapper = document.createElement('div');
  wrapper.className = 'auth-field';
  const labelEl = document.createElement('label');
  labelEl.className = 'auth-label';
  labelEl.textContent = label;
  labelEl.setAttribute('for', `auth-fp-${name}`);
  const input = document.createElement('input');
  input.className = 'auth-input';
  input.type = type;
  input.id = `auth-fp-${name}`;
  input.name = name;
  input.autocomplete = autocomplete as AutoFill;
  input.setAttribute('aria-label', label);
  wrapper.append(labelEl, input);
  return { wrapper, input };
}
